import React, { useMemo, useState } from "react";

import Table from "../../Components/Table";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  resetGuidancePreview,
  setGuidancePreviewDay,
  setGuidancePreviewFirstQuarter,
  setGuidancePreviewFirstWeek,
  updateGuidanceCadenceSchedule,
  updateGuidanceHubEnabled,
} from "../../Store/Reducers/GuidanceSlice";
import {
  CadenceKey,
  buildScheduleFromDayAssignments,
  getCadenceForDay,
  getTodayCadence,
} from "../../Utils/Guidance/guidanceCadence";
import {
  CADENCE_SCHEDULE_ASSIGNABLE,
  GUIDANCE_HUB_ROLE_OPTIONS,
} from "../../Utils/Guidance/guidanceHubSettings";
import { GUIDANCE_ROLES } from "../../Utils/Guidance/guidanceRole";

const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const CADENCE_LABEL: Record<Exclude<CadenceKey, "biweekly">, string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly*",
  quarterly: "Quarterly*",
};

/**
 * IzbaAdmin > Hub Settings — platform-wide Playbook Hub kill switch + the
 * Cadence Schedule (day-of-week -> cadence bucket) editor, plus a
 * client-side-only "Preview as day" simulator (guidance-hub design-note.md
 * §10.1.1).
 */
const OrgSettingsTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    hubEnabled,
    hubEnabledRoles,
    hubEnabledSaving,
    cadenceSchedule,
    cadenceScheduleSaving,
    previewDayOverride,
    previewFirstWeekOverride,
    previewFirstQuarterOverride,
  } = useAppSelector(state => state.guidance);

  const [previewDaySelection, setPreviewDaySelection] = useState<string>("");

  const handleDayChange = (
    day: number,
    cadence: Exclude<CadenceKey, "biweekly">
  ) => {
    const assignments: Record<number, Exclude<CadenceKey, "biweekly">> = {};
    for (let d = 0; d <= 6; d++) {
      assignments[d] =
        d === day
          ? cadence
          : (getCadenceForDay(cadenceSchedule, d) as Exclude<
              CadenceKey,
              "biweekly"
            >);
    }
    dispatch(
      updateGuidanceCadenceSchedule(
        buildScheduleFromDayAssignments(assignments)
      )
    );
  };

  const previewActive =
    previewDayOverride !== null ||
    previewFirstWeekOverride ||
    previewFirstQuarterOverride;

  const toggleHubEnabled = () =>
    dispatch(
      updateGuidanceHubEnabled({ enabled: !hubEnabled, roles: hubEnabledRoles })
    );

  const toggleRole = (role: string) =>
    dispatch(
      updateGuidanceHubEnabled({
        enabled: hubEnabled,
        roles: { ...hubEnabledRoles, [role]: !hubEnabledRoles[role] },
      })
    );

  const roleHubRows = useMemo(
    () =>
      GUIDANCE_HUB_ROLE_OPTIONS.map(({ role, label }) => {
        const on = hubEnabledRoles[role] === true;
        const roleMeta = GUIDANCE_ROLES.find(r => r.key === role);
        return {
          role,
          label,
          playbooksShown: on ? `${roleMeta?.label ?? role} playbooks` : "—",
          on,
        };
      }),
    [hubEnabledRoles]
  );

  const roleHubColumns = useMemo(
    () => [
      {
        key: "label",
        title: "Role",
        width: "280px",
        render: (value: string) => (
          <span className="font-medium text-gray-900">{value}</span>
        ),
      },
      {
        key: "playbooksShown",
        title: "Playbooks shown",
        render: (value: string) => (
          <span className="text-xs text-gray-500">{value}</span>
        ),
      },
      {
        key: "status",
        title: "Status",
        textalign: "right" as const,
        width: "140px",
        render: (_: unknown, record: { role: string; on: boolean }) => (
          <button
            type="button"
            disabled={hubEnabledSaving}
            onClick={() => toggleRole(record.role)}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3.5 py-1.5 whitespace-nowrap transition-colors disabled:opacity-50 ${
              record.on
                ? "bg-green-50 text-green-700 border border-green-200 hover:bg-green-100"
                : "bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100"
            }`}
          >
            {record.on ? "● Enabled" : "○ Disabled"}
          </button>
        ),
      },
    ],
    [hubEnabledSaving, hubEnabled, hubEnabledRoles]
  );

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Org Settings</h3>
        <p className="mt-1 text-sm text-gray-500">
          Platform-wide controls for the playbook guidance experience.
        </p>
      </div>

      <section className="rounded-xl border border-gray-200 bg-white">
        <div className="flex items-start justify-between gap-6 px-5 py-4">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900">
              Enable Playbook Hub
            </div>
            <p className="mt-1 text-sm text-gray-500 leading-relaxed max-w-2xl">
              Turns on the daily modal, home widget, guide drawer, and cadence
              map for all users. When off, the hub is hidden for everyone.
            </p>
          </div>
          <button
            type="button"
            disabled={hubEnabledSaving}
            onClick={toggleHubEnabled}
            className={`shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3.5 py-1.5 whitespace-nowrap transition-colors ${
              hubEnabled
                ? "bg-green-50 text-green-700 border border-green-200 hover:bg-green-100"
                : "bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100"
            } ${hubEnabledSaving ? "opacity-60 cursor-wait" : ""}`}
          >
            {hubEnabled ? "● Enabled" : "○ Disabled"}
          </button>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h4 className="text-sm font-semibold text-gray-900">
            Playbook Hub by Role
          </h4>
          <p className="mt-1 text-sm text-gray-500 leading-relaxed max-w-3xl">
            Turn the Playbook Hub on or off for individual roles. A disabled
            role sees no entry points — no home widget, daily modal, guide
            drawer, or Guidance Library banner — and the Playbooks Hub page is
            closed to them.
            {!hubEnabled && (
              <span className="block mt-1 font-semibold text-amber-700">
                The org-wide switch above is off, so the Hub is dark for
                everyone regardless of these.
              </span>
            )}
          </p>
        </div>

        <div className="px-2 pb-2">
          <Table
            columns={roleHubColumns}
            data={roleHubRows}
            pagination={false}
            rowKey={record => record.role}
          />
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h4 className="text-sm font-semibold text-gray-900">
            Cadence Schedule
          </h4>
          <p className="mt-1 text-sm text-gray-500 leading-relaxed max-w-3xl">
            Map each day of the week to its primary playbook cadence. Priority
            on overlap: Quarterly &gt; Monthly &gt; Weekly &gt; Daily. Monthly
            and Quarterly also require the first-week-of-month /
            first-week-of-quarter condition.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                <th className="px-5 py-3 w-40">Day</th>
                <th className="px-5 py-3 w-52">Primary cadence</th>
                <th className="px-5 py-3">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {DAY_NAMES.map((dayName, day) => (
                <tr key={day} className="hover:bg-gray-50/60">
                  <td className="px-5 py-3 font-medium text-gray-900">
                    {dayName}
                  </td>
                  <td className="px-5 py-3">
                    <select
                      value={getCadenceForDay(cadenceSchedule, day)}
                      disabled={cadenceScheduleSaving}
                      onChange={e =>
                        handleDayChange(
                          day,
                          e.target.value as Exclude<CadenceKey, "biweekly">
                        )
                      }
                      className="min-w-[140px] text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 disabled:opacity-50"
                    >
                      {CADENCE_SCHEDULE_ASSIGNABLE.map(cadence => (
                        <option key={cadence} value={cadence}>
                          {CADENCE_LABEL[cadence]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-5 py-3 text-xs text-gray-500">
                    {cadenceSchedule.quarterly.includes(day) && (
                      <div className="font-medium text-green-700">
                        1st week of quarter → Quarterly (highest priority)
                      </div>
                    )}
                    {cadenceSchedule.monthly.includes(day) && (
                      <div className="font-medium text-amber-700">
                        1st week of month → Monthly
                      </div>
                    )}
                    {!cadenceSchedule.quarterly.includes(day) &&
                      !cadenceSchedule.monthly.includes(day) && (
                        <span className="text-gray-300">—</span>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/80">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="text-xs font-semibold text-gray-700">
              Preview as day
            </span>
            <select
              value={previewDaySelection}
              onChange={e => setPreviewDaySelection(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300"
            >
              <option value="">Select day…</option>
              {DAY_NAMES.map((dayName, day) => (
                <option key={day} value={day}>
                  {dayName}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={previewDaySelection === ""}
              onClick={() =>
                dispatch(setGuidancePreviewDay(Number(previewDaySelection)))
              }
              className="text-xs font-semibold text-white bg-[#974ae4] hover:bg-[#8538d4] disabled:opacity-40 disabled:cursor-not-allowed rounded-lg px-3 py-1.5 transition-colors"
            >
              Apply Preview
            </button>
            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={previewFirstWeekOverride}
                onChange={e =>
                  dispatch(setGuidancePreviewFirstWeek(e.target.checked))
                }
                className="rounded border-gray-300 text-[#974ae4] focus:ring-purple-200"
              />
              1st week of month
            </label>
            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={previewFirstQuarterOverride}
                onChange={e =>
                  dispatch(setGuidancePreviewFirstQuarter(e.target.checked))
                }
                className="rounded border-gray-300 text-[#974ae4] focus:ring-purple-200"
              />
              1st week of quarter
            </label>
            <button
              type="button"
              onClick={() => {
                dispatch(resetGuidancePreview());
                setPreviewDaySelection("");
              }}
              className="text-xs font-semibold text-gray-600 border border-gray-200 bg-white rounded-lg px-3 py-1.5 hover:bg-gray-50 hover:border-gray-300 transition-colors"
            >
              Reset to today
            </button>
          </div>
          {previewActive && (
            <p className="mt-3 text-xs text-gray-600">
              Simulating{" "}
              <span className="font-semibold text-gray-800">
                {previewDayOverride !== null
                  ? DAY_NAMES[previewDayOverride]
                  : "today"}
              </span>
              {previewFirstWeekOverride && " · 1st week of month"}
              {previewFirstQuarterOverride && " · 1st week of quarter"}
              {" → "}
              <span className="font-semibold text-[#974ae4]">
                {getTodayCadence(
                  new Date(),
                  cadenceSchedule,
                  previewDayOverride,
                  previewFirstWeekOverride,
                  previewFirstQuarterOverride
                )}
              </span>{" "}
              playbook
            </p>
          )}
        </div>
      </section>

      <section>
        <div className="mb-3">
          <h4 className="text-sm font-semibold text-gray-900">
            Upcoming settings
          </h4>
          <p className="mt-0.5 text-xs text-gray-400">Coming soon</p>
        </div>
        <div className="space-y-3 opacity-50 pointer-events-none">
          <div className="flex items-start justify-between gap-6 rounded-xl border border-gray-200 bg-white px-5 py-4">
            <div>
              <div className="text-sm font-semibold text-gray-900">
                Habit Score Visibility
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Allow end users to see their own engagement habit score. When
                off, habit scores are visible to admins and program leaders
                only.
              </p>
            </div>
            <span className="shrink-0 text-xs font-semibold rounded-full px-3.5 py-1.5 bg-gray-50 text-gray-400 border border-gray-200">
              ○ Disabled
            </span>
          </div>
          <div className="flex items-start justify-between gap-6 rounded-xl border border-gray-200 bg-white px-5 py-4">
            <div>
              <div className="text-sm font-semibold text-gray-900">
                PostHog Analytics Dashboard
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Show the in-app playbook engagement analytics dashboard to
                program leaders and admins.
              </p>
            </div>
            <span className="shrink-0 text-xs font-semibold rounded-full px-3.5 py-1.5 bg-gray-50 text-gray-400 border border-gray-200">
              ○ Disabled
            </span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OrgSettingsTab;
